import { useCallback, useMemo } from 'react';
import { Panel } from '../Panel.tsx';
import { duration, NO_VALUE } from '../format.ts';
import { levelTone, STATUS_STATE } from '../state.ts';
import { Figure } from '../states/Figure.tsx';
import { unread, type Read } from '../states/read.ts';
import type { RobotStatus } from '../../hooks/useRobots.ts';
import type { TelemetryReading } from '../../hooks/useTelemetry.ts';
import { axisClock, ticks } from '../timeScale.ts';
import type { Channel, TimeAxis } from './channels.ts';
import { stampOf } from './channels.ts';
import { CursorOverlay } from './CursorOverlay.tsx';
import { CursorStamp } from './CursorStamp.tsx';
import { useCursor } from './cursor.ts';
import type { EventMark } from './events.ts';
import {
  captionRoom, frameOf, limitCallouts, MIN_PITCH, monoFits, monoWidth, placeCallouts,
  type Callout, type Frame, type LimitCallout,
} from './frame.ts';
import { GutterCallout } from './GutterCallout.tsx';
import { usePlotSurface, xScale } from './plotSurface.ts';
import {
  buildSegments, channelBox, lastIndex, quoteAt, runAt, samplesIn, valueRuns, yTicks,
  type Box, type Run, type Samples,
} from './scale.ts';
import type { ChartSeries } from './TimeSeriesCard.tsx';
import { useMeasure } from './useMeasure.ts';
import styles from './charts.module.css';

/* ==========================================================================
   The signal stack — every channel of ONE robot as a lane on ONE x-axis.

   This is the instrument the console is actually for. A grid of cards puts
   three plots side by side, and side-by-side plots cannot be read against
   each other: the operator asking "did the CPU spike when it started
   climbing?" has to carry an x position across 500px of panel seam and
   trust their eye. Stacked lanes answer it by construction — one window,
   one x-scale, one crosshair through all of them, and a vertical read at
   any instant crosses every channel at once. It is what
   `foxglove-state-transitions-timeline.png` does with five discrete series,
   what `auterion-flight-log-review.png` does with its channel index, and
   what `rerun-mcap-lidar-dual-3d-timeline.png` does under its viewports.

   THE PLOT IS A BOX AND THE WRITING IS IN GUTTERS. Nothing is printed over
   a trace — not the lane's name, not the y-box's ends, not the value at the
   cursor, not a threshold's number. See `frame.ts` for why, and for the
   arithmetic. The consequence worth naming here: every lane's value and
   every lane's threshold labels right-align on ONE pair of columns down the
   whole stack, so nine channels read as a column of numbers rather than as
   nine separately-typeset plots.

   THE MODE RIBBON IS PART OF THE STACK, not a panel above it. §5: InOrbit
   "paints a discrete mode ribbon under the metric time axis so state and
   metric history share one x-scale". Ours is the top lane of the same SVG,
   so there is not even a 3px seam between `charging → active` and the
   battery trace that explains it.

   Every rule the kit keeps, this keeps: a coverage hole is a hole, a limit
   is a rule on the trace, a wrapping quantity breaks at its seam, and a
   readout more than one cadence from the cursor is `—`. The arithmetic is
   `scale.ts`'s, unchanged and shared with the card grid, so the two views of
   the same window cannot disagree.

   ONE ROBOT. A lane is a channel here, so a second robot would have to be
   either a second trace in the same lane — overplotting, which the brief
   bans and which is unreadable the moment two machines cross — or a second
   set of nine lanes. Comparison is the card grid's job, where a card is a
   channel and a lane is a robot. The workspace switches between them and
   says why.
   ========================================================================== */

/** The mode band. --h-ribbon is 28; inside a stack it gives 6px back. */
const RIBBON_H = 22;
/** A lane below this cannot carry its name, its range and a trace. */
const MIN_LANE = 44;
/** Baseline of the lane's first gutter line — name left, value right. */
const NAME_DY = 12;
/** Baseline of the second gutter line — the range the window held. */
const RANGE_DY = 23;
/** Baseline of the third — the published domain, where the box is not it. */
const DOMAIN_DY = 34;
/** One line of gutter type. Two labels closer than this are one smudge. */
const LINE_H = MIN_PITCH;
/** The gap between the stats block's label column and its value column. */
const STAT_GAP = 6;
/** Digits the stats value column reserves, so it cannot jitter as data ticks. */
const STAT_DIGITS = 5;
/** Below this a mode segment cannot carry its own name. */
const LABEL_MIN_PX = 52;
/** Below this the name fits but its dwell time does not. */
const DWELL_MIN_PX = 132;
const HAIRLINE_PX = 2;
/** Breathing room above and below a lane's trace. */
const TOP_PAD = 2;
const BOT_PAD = 4;

/** A stable identity for "this stack has no robot", so the memos do not churn. */
const EMPTY_READINGS: readonly TelemetryReading[] = [];

function modeTone(state: string | null): { tone: string; label: string } {
  if (state === null) return { tone: 'neutral', label: 'unknown' };
  const d = STATUS_STATE[state as RobotStatus];
  return d === undefined ? { tone: 'neutral', label: state } : { tone: d.tone, label: state };
}

function tickLabel(v: number, span: number, max: number): string {
  const digits = span >= 20 ? 0 : span >= 2 ? 1 : 2;
  return v.toFixed(Math.min(digits, max));
}

/** n / min / median / max over the window — the distribution, as numbers. */
function stats(samples: Samples, digits: number): [string, string][] | null {
  const v: number[] = [];
  for (const x of samples.v) if (x !== null) v.push(x);
  if (v.length < 2) return null;
  v.sort((a, b) => a - b);
  const mid = v.length >> 1;
  const p50 = v.length % 2 === 1 ? v[mid]! : (v[mid - 1]! + v[mid]!) / 2;
  return [
    ['n', String(v.length)],
    ['min', v[0]!.toFixed(digits)],
    ['p50', p50.toFixed(digits)],
    ['max', v[v.length - 1]!.toFixed(digits)],
  ];
}

function worstOf(lv: readonly ('ok' | 'warn' | 'bad' | 'unknown')[]): 'ok' | 'warn' | 'bad' | 'unknown' {
  let seen = false;
  let worst: 'ok' | 'warn' = 'ok';
  for (const l of lv) {
    if (l === 'unknown') continue;
    seen = true;
    if (l === 'bad') return 'bad';
    if (l === 'warn') worst = 'warn';
  }
  return seen ? worst : 'unknown';
}

export function SignalStack({
  channels,
  series,
  axis,
  mode = true,
  title = 'Signal stack',
  tools,
  notice,
  read,
  events,
}: {
  /** The lanes, top to bottom. */
  channels: readonly Channel[];
  /** One robot. Anything past the first is ignored — see the note above. */
  series: readonly ChartSeries[];
  axis: TimeAxis;
  /** Draw the agent's `state` field as the stack's top lane. */
  mode?: boolean;
  title?: string;
  tools?: React.ReactNode;
  /* An inline row between the header and the lanes: the panel keeps its
     shape and its axes and says what is wrong on one line, rather than being
     replaced by a centred message (§6, empty/error states). */
  notice?: React.ReactNode;
  /**
   * THE READ THE TRACES CAME FROM. `0 transitions` is counted from the mode
   * lane, and a robot that never changed mode and an endpoint that never
   * answered produce the same zero. Required, so a new caller cannot
   * accidentally reintroduce the count.
   */
  read: Read;
  /* Commands issued to this robot, drawn at their true timestamps across
     every lane — so "did it fault after we sent that" is a vertical read
     rather than two routes and an arithmetic (§5 timelines). */
  events?: readonly EventMark[] | undefined;
}) {
  const cursor = useCursor();
  const [bodyRef, box] = useMeasure<HTMLDivElement>();
  const subject = series[0];
  const readings = useMemo(() => subject?.readings ?? EMPTY_READINGS, [subject]);
  const colour = subject?.colour ?? 'var(--text-mid)';

  const samples = useMemo(
    () => channels.map((c) => samplesIn(readings, c, cursor.from, cursor.to, axis)),
    [channels, readings, cursor.from, cursor.to, axis],
  );
  const boxes = useMemo(
    () => channels.map((c, i) => channelBox(c, samples[i]!)),
    [channels, samples],
  );

  /* The mode lane, built from the same rows and broken by the same holes as
     every trace above it. */
  const modeLane = useMemo(() => {
    const t: number[] = [];
    const v: (string | null)[] = [];
    for (let i = readings.length - 1; i >= 0; i -= 1) {
      const r = readings[i]!;
      const ts = stampOf(r, axis);
      if (!Number.isFinite(ts) || ts < cursor.from || ts > cursor.to) continue;
      t.push(ts);
      v.push(r.state);
    }
    const deltas: number[] = [];
    for (let i = 1; i < t.length; i += 1) deltas.push(t[i]! - t[i - 1]!);
    deltas.sort((a, b) => a - b);
    const cad = deltas.length > 0 ? deltas[deltas.length >> 1]! : 0;
    return {
      cadence: cad,
      /* The newest READING, not the newest run's end: a run is extended by one
         cadence so it covers the interval its last sample stands for, and
         quoting that as "when the robot last spoke" would make the caption's
         age disagree with the rail's by exactly one cadence. */
      last: t[t.length - 1] ?? null,
      runs: valueRuns(t, v, cad > 0 ? cad * 2.5 : Infinity, cad) as Run<string | null>[],
    };
  }, [readings, axis, cursor.from, cursor.to]);

  const cadence = samples.find((s) => s.cadence > 0)?.cadence ?? modeLane.cadence ?? 60_000;

  const w = box.w;
  const frame = useMemo(() => frameOf(Math.max(1, w)), [w]);
  const surface = usePlotSurface({
    stepMs: cadence > 0 ? cadence : 60_000,
    padL: frame.padL,
    padR: frame.padR,
  });

  /* Composed refs have to be stable — see useMeasure. */
  const { setBox } = surface;
  const attach = useCallback(
    (el: HTMLDivElement | null) => {
      bodyRef(el);
      setBox(el);
    },
    [bodyRef, setBox],
  );

  const availH = Math.max(1, box.h);
  const ribbonH = mode && modeLane.runs.length > 0 ? RIBBON_H : 0;
  const n = channels.length;
  /* The lanes divide the panel. Below the floor they stop shrinking and the
     body scrolls instead — a 12px lane is a line, not a plot. */
  const laneH = n === 0 ? 0 : Math.max(MIN_LANE, Math.floor((availH - ribbonH) / n));
  const svgH = Math.max(availH, ribbonH + n * laneH);
  /* Below the floor the lanes stop shrinking and the body scrolls (see
     `.stackBody`). A lane cut by the panel's edge with nothing saying so is
     a lane the operator does not know exists, so the caption counts them. */
  const hidden = Math.max(0, n - Math.floor(Math.max(0, availH - ribbonH) / laneH));
  const x = xScale(cursor.from, cursor.to, frame.plotW);
  const xt = ticks(cursor.from, cursor.to, Math.max(3, Math.round(frame.plotW / 130)));

  /* WHAT THE READOUTS QUOTE WHEN NOBODY IS POINTING AT ANYTHING.

     With a cursor on the plot the answer is that instant, or nothing (see
     `quoteAt`). With no cursor the answer is the newest reading the window
     holds — because "what is it doing now" is the question the resting page
     is being asked, and a robot four cadences stale is the ordinary case, not
     an error. The cost of that is one obligation: say how old it is. It is
     said once, here, in the panel's own caption, rather than nine times down
     the lanes (§13 tell 24). */
  const newest = useMemo(() => {
    let m: number | null = null;
    for (const s of samples) {
      const i = lastIndex(s);
      if (i >= 0 && (m === null || s.t[i]! > m)) m = s.t[i]!;
    }
    const tail = modeLane.last;
    if (tail !== null && (m === null || tail > m)) m = tail;
    return m;
  }, [samples, modeLane]);

  const at = cursor.at ?? newest ?? cursor.to;
  const age = cursor.at === null && newest !== null ? cursor.to - newest : 0;
  const stale = age > (cadence > 0 ? cadence : 60_000);
  const activeRun = runAt(modeLane.runs, at, cadence);
  const transitions = Math.max(0, modeLane.runs.length - 1);
  /* Runs too thin to draw as anything but a hairline. Admitted in the
     panel's caption rather than as text laid over the band's last segment —
     the ribbon prints its values INSIDE its segments by design (§5), and a
     note is not a value. */
  const overZoomed = useMemo(
    () =>
      ribbonH > 0
      && modeLane.runs.some(
        (r) => Math.min(frame.plotW, x(r.end)) - Math.max(0, x(r.start)) < HAIRLINE_PX,
      ),
    [modeLane.runs, x, frame.plotW, ribbonH],
  );

  /* THE CROSSHAIR, OUT LOUD.

     ←/→ move the playhead, and without this the only thing that changes for
     someone reading by ear is a `<title>`-less SVG they cannot enter. So the
     stack keeps a polite live region carrying exactly what the gutters
     carry: the instant, the mode, and every channel's value at it.

     Bound to the PLAYHEAD and not to `at` deliberately — a pointer sweeping
     the plot would otherwise queue an utterance per pixel, which is not
     assistance, it is a denial of service. The playhead moves on a key, a
     click or a step: one deliberate act, one announcement. */
  const spoken = useMemo(() => {
    const t = cursor.playhead;
    if (t === null) return '';
    const parts: string[] = [axisClock(t)];
    if (activeRun !== null) parts.push(`mode ${modeTone(activeRun.value).label}`);
    for (let k = 0; k < channels.length; k += 1) {
      const c = channels[k]!;
      const s = samples[k]!;
      const { i } = quoteAt(s, t, cursor.to);
      const v = i >= 0 ? s.v[i] : null;
      parts.push(
        v == null
          ? `${c.label}, no reading`
          : `${c.label} ${v.toFixed(c.digits)} ${c.unit}`,
      );
    }
    return parts.join('. ');
  }, [cursor.playhead, cursor.to, channels, samples, activeRun]);

  /* WHERE THE PLAYHEAD'S FLAG GOES ON THE RULER.

     Measured rather than transformed, because the ticks it displaces have to
     be able to ask where it is. It hangs to the right of the cursor in the
     window's first half and to the left in its second, so it never runs off
     the panel's edge. */
  const head = useMemo(() => {
    if (cursor.at === null || cursor.at < cursor.from || cursor.at > cursor.to) return null;
    const label = axisClock(cursor.at);
    const w = Math.round(monoWidth(label.length, 10)) + 6;
    const cx = frame.x0 + x(cursor.at);
    return { label, w, left: x(cursor.at) < frame.plotW / 2 ? cx + 1 : cx - w - 1 };
  }, [cursor.at, cursor.from, cursor.to, frame.x0, frame.plotW, x]);

  return (
    <Panel
      title={title}
      count={n + (ribbonH > 0 ? 1 : 0)}
      meta={
        /* `device · stream · HH:MM:SSZ` — the caption every Formant tile
           carries, so the instant these lanes are quoting is on the tile and
           not only in the transport bar. */
        <span className={styles.cardMeta}>
          {subject !== undefined && (
            <span className={styles.metaRange}>{subject.id}</span>
          )}
          <span className={styles.metaRange}><Figure of={read} value={transitions} /></span>
          <span className={styles.metaUnit}>
            {transitions === 1 ? 'transition' : 'transitions'}
          </span>
          <span
            className={styles.metaValue}
            data-tone={activeRun === null ? 'neutral' : modeTone(activeRun.value).tone}
          >
            {activeRun === null ? NO_VALUE : modeTone(activeRun.value).label}
          </span>
          {/* The instant every readout on this panel is quoting, and — when
              that instant is not current — how old it is. Said once here
              rather than as a suffix on nine lanes (§13 tell 24). */}
          {newest === null ? (
            /* `no readings` is a claim about the WINDOW; withheld where the
               source behind it never answered, because then the console has
               not looked. The notice row below carries the fault. */
            <span className={styles.metaUnit}>{unread(read) ? NO_VALUE : 'no readings'}</span>
          ) : (
            <CursorStamp at={at} format={axisClock} />
          )}
          {stale && (
            <span className={styles.metaValue} data-tone="warn">{`${duration(age)} old`}</span>
          )}
          {overZoomed && <span className={styles.metaUnit}>zoom for more</span>}
          {hidden > 0 && (
            <span
              className={styles.metaUnit}
              title="The panel is shorter than the lanes it is drawing. Scroll the stack, or hide channels in the rail."
            >
              {`${hidden} more below`}
            </span>
          )}
        </span>
      }
      tools={tools}
      pad="none"
      scroll={false}
      className={`${styles.card} ${styles.stackCard}`}
    >
      {/* AN EMPTY WINDOW KEEPS THE INSTRUMENT.

          §11.6 state 1 is the model — "vitals tracks drawn with no arc and
          `—`". A robot that has never reported still has nine channels and
          the operator still needs to see which ones they are; replacing the
          lanes with a centred sentence changes the shape of the surface
          because a machine is silent, and §8 tell 3 is explicit that an
          empty state's height must not differ from a populated one's. So the
          lanes, the captions and the axis stay, every readout says `—`, and
          the fact is one row under the header. */}
      {notice !== undefined && <div className={styles.notice}>{notice}</div>}
      <div
        className={styles.stackBody}
        ref={attach}
        role="group"
        aria-label={
          `${n} telemetry channels${ribbonH > 0 ? ' and the mode ribbon' : ''} on one time axis`
          + `, ${new Date(cursor.from).toISOString()} to ${new Date(cursor.to).toISOString()}.`
          + ' Arrow keys move the cursor, drag to zoom.'
        }
        tabIndex={0}
        {...surface.handlers}
      >
        {w > 0 && (
              <svg width={w} height={svgH} aria-hidden="true">
                {/* The plot's two vertical edges. The gutters are chrome and
                    the box between them is the picture; without the seam the
                    two read as one field and the eye stops trusting which
                    side of it a number belongs to. */}
                <line
                  x1={frame.x0 + 0.5} x2={frame.x0 + 0.5} y1={0} y2={svgH}
                  stroke="var(--line-2)" strokeWidth={1} shapeRendering="crispEdges"
                />
                <line
                  x1={frame.x1 + 0.5} x2={frame.x1 + 0.5} y1={0} y2={svgH}
                  stroke="var(--line-2)" strokeWidth={1} shapeRendering="crispEdges"
                />

                <g transform={`translate(${frame.x0},0)`}>
                  {xt.map((t) => (
                    <line
                      key={`x${t.t}`}
                      x1={Math.round(t.f * frame.plotW) + 0.5}
                      x2={Math.round(t.f * frame.plotW) + 0.5}
                      y1={0}
                      y2={svgH}
                      stroke="var(--plot-grid)"
                      strokeWidth={1}
                      shapeRendering="crispEdges"
                    />
                  ))}
                </g>

                {ribbonH > 0 && (
                  <ModeRibbon
                    runs={modeLane.runs}
                    height={RIBBON_H}
                    frame={frame}
                    x={x}
                    from={cursor.from}
                    to={cursor.to}
                    active={activeRun}
                  />
                )}

                {/* EVENTS, ON THE SAME AXIS AS THE SIGNALS.

                    Under the traces, not over them: the mark's job is to say
                    WHEN, and a dispatch rule that hides the sample it is
                    supposed to be explained by has taken the reading's place.
                    The diamond sits on the ribbon's top edge, clear of the
                    segment labels below it. */}
                {events !== undefined
                  && events.map((ev) => {
                    const ex = x(ev.at);
                    if (ex < 0 || ex > frame.plotW) return null;
                    const cx = frame.x0 + Math.round(ex) + 0.5;
                    return (
                      <g key={ev.key} data-tone={ev.tone}>
                        <title>{ev.detail}</title>
                        <line
                          x1={cx} x2={cx} y1={0} y2={svgH}
                          stroke="var(--tone)" strokeOpacity={0.3} strokeWidth={1}
                          strokeDasharray="2 3" shapeRendering="crispEdges"
                        />
                        <path
                          d={`M${cx},0 L${cx + 3.5},3.5 L${cx},7 L${cx - 3.5},3.5 Z`}
                          fill="var(--tone)"
                        />
                      </g>
                    );
                  })}

                {channels.map((c, i) => (
                  <StackLane
                    key={c.key}
                    top={ribbonH + i * laneH}
                    laneH={laneH}
                    frame={frame}
                    channel={c}
                    samples={samples[i]!}
                    box={boxes[i]!}
                    colour={colour}
                    x={x}
                    at={cursor.at}
                    edge={cursor.to}
                  />
                ))}

                <g transform={`translate(${frame.x0},0)`}>
                  <CursorOverlay
                    from={cursor.from}
                    to={cursor.to}
                    width={frame.plotW}
                    height={svgH}
                    at={cursor.at}
                    playhead={cursor.playhead}
                    brush={surface.brush}
                  />
                </g>
          </svg>
        )}
      </div>
      <span className="f3-sr-only" role="status" aria-live="polite">{spoken}</span>
      <div className={styles.ruler}>
        {xt.map((t) => {
          const tx = frame.x0 + t.f * frame.plotW;
          /* A tick the playhead's flag would print through is dropped —
             `14:10:17Z` laid over `14:00` reads as `14:10:17Z0`, which is a
             number the axis never had. The hour is one tick out of six; the
             instant under the cursor is the thing being asked for. */
          if (
            head !== null
            && tx + 3 + monoWidth(t.label.length, 10) > head.left - 6
            && tx < head.left + head.w + 6
          ) return null;
          return (
            <span key={t.t} className={styles.rulerTick} style={{ left: `${tx}px` }}>
              {t.label}
            </span>
          );
        })}
        {/* THE CROSSHAIR'S OWN INSTANT, ON THE AXIS THE CROSSHAIR IS ON.

            The panel's caption already stamps it and so does the ledger, but
            both are a thousand pixels from the eye, which is on the trace.
            Every reference that scrubs puts the time on the ruler under the
            playhead — `foxglove-event-editor-timeline-markers.png`,
            `formant-timeline-data-presence-scrubber.png` — and §5 asks for
            it outright: "the current time is centred and monospace". It takes
            the ink of the cursor it belongs to: neutral while the pointer is
            sweeping, accent once the operator has pinned it. */}
        {head !== null && (
          <span
            className={styles.rulerHead}
            data-pinned={cursor.hover === null}
            style={{ left: `${head.left}px`, width: `${head.w}px` }}
          >
            {head.label}
          </span>
        )}
      </div>
    </Panel>
  );
}

/* --------------------------------------------------------------------------
   The mode ribbon — the stack's top lane.
   -------------------------------------------------------------------------- */

function ModeRibbon({
  runs, height, frame, x, from, to, active,
}: {
  runs: readonly Run<string | null>[];
  height: number;
  frame: Frame;
  x: (t: number) => number;
  from: number;
  to: number;
  active: Run<string | null> | null;
}) {
  const w = frame.plotW;
  return (
    <g>
      {/* The ribbon's own gutter entries, in the same two columns every lane
          below it uses: what the band is on the left, what it reads at the
          cursor on the right. */}
      <text className={styles.gutterName} x={frame.nameX} y={height - 7}>Mode</text>
      <text
        className={styles.gutterValue}
        x={frame.valueX}
        y={height - 7}
        textAnchor="end"
        data-tone={active === null ? 'neutral' : modeTone(active.value).tone}
      >
        {active === null ? NO_VALUE : modeTone(active.value).label}
      </text>
      <g transform={`translate(${frame.x0},0)`}>
        <rect x={0} y={0} width={w} height={height} fill="var(--bg-0)" />
        {runs.map((r) => {
          const a = Math.max(0, x(r.start));
          const b = Math.min(w, x(r.end));
          const bw = Math.max(1, b - a);
          const t = modeTone(r.value);
          return (
            <g key={`${r.start}-${String(r.value)}`} data-tone={t.tone}>
              <rect
                x={a} y={0} width={bw} height={height}
                fill="var(--tone)" fillOpacity={0.26} shapeRendering="crispEdges"
              />
              <rect
                x={a} y={height - 2} width={bw} height={2}
                fill="var(--tone)" shapeRendering="crispEdges"
              />
              {bw >= LABEL_MIN_PX && (
                <text className={styles.segLabel} x={a + 5} y={height - 7}>
                  {t.label}
                  {bw >= DWELL_MIN_PX && (
                    <tspan className={styles.segDwell}>
                      {`  ${duration(Math.min(r.end, to) - Math.max(r.start, from))}`}
                    </tspan>
                  )}
                </text>
              )}
            </g>
          );
        })}
        <line
          x1={0} x2={w} y1={height} y2={height}
          stroke="var(--line-2)" strokeWidth={1} shapeRendering="crispEdges"
        />
      </g>
    </g>
  );
}

/* --------------------------------------------------------------------------
   One channel's lane.
   -------------------------------------------------------------------------- */

function StackLane({
  top, laneH, frame, channel, samples, box, colour, x, at, edge,
}: {
  top: number;
  laneH: number;
  frame: Frame;
  channel: Channel;
  samples: Samples;
  box: Box;
  colour: string;
  x: (t: number) => number;
  /** The cursor's instant, or null when nobody is pointing at the plot. */
  at: number | null;
  /** The window's right edge — what a quoted sample's age is measured from. */
  edge: number;
}) {
  const width = frame.plotW;
  const y0 = top + TOP_PAD;
  const plotH = laneH - TOP_PAD - BOT_PAD;
  const span = box.hi - box.lo || 1;
  const y = (v: number) => y0 + plotH - ((v - box.lo) / span) * plotH;

  const trace = useMemo(
    () => buildSegments(samples, x, y, channel.wrap),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [samples, box.lo, box.hi, plotH, y0, width, channel.wrap],
  );

  const { i, stale } = quoteAt(samples, at, edge);
  /* Ten pixels between readings is where a dot stops being noise and starts
     being a measurement. Below that the trace is the honest rendering. */
  const sparse = samples.t.length > 1 && width / (samples.t.length - 1) >= 10;
  const cur = i >= 0 ? samples.v[i] : null;
  const curLevel = i >= 0 ? samples.lv[i]! : 'unknown';
  const verdict = worstOf(samples.lv);
  const statRows = stats(samples, channel.digits);

  /* Only limits the channel is entitled to DRAW. Disk free is judged on disk
     used, so a rule in GiB would be a number the contract never published. */
  const limits =
    channel.judge !== undefined
      ? []
      : ([
          channel.warn !== undefined ? { v: channel.warn, kind: 'warn' as const } : null,
          channel.bad !== undefined ? { v: channel.bad, kind: 'bad' as const } : null,
        ].filter((l): l is { v: number; kind: 'warn' | 'bad' } => l !== null));
  const crossed = { warn: verdict !== 'ok' && verdict !== 'unknown', bad: verdict === 'bad' };

  /* THE RIGHT GUTTER IS ONE COLUMN OF CALLOUTS AND EVERY ONE IS POSITIONED.

     The reading and the limits are laid out together, each anchored to the
     height it is about — the trace's own point for the reading, the rule for
     a limit — then pushed apart only as far as legibility demands, with a
     hairline leading from the anchor to any label that had to move. What
     this replaced pinned the reading to `top + NAME_DY`, which put a
     faulted battery's 7.9 % ABOVE the 40 % and 15 % rules it had fallen
     through and gave the operator no cue that two of those three numbers
     meant something by their position and one did not. See `frame.ts`.

     Rule COLOUR is the rule's meaning and does not depend on whether this
     window happened to breach: amber is the warn limit and red is the error
     limit whatever the trace did. What the breach changes is the RULE's
     weight — a crossed limit draws at full strength, an uncrossed one at
     40% — so "there is a limit here" and "you are over it" stay
     distinguishable without spending a second hue. */
  const valueAnchor = cur == null ? null : y(cur);
  const placed = placeCallouts<Callout | LimitCallout>(
    [
      /* A window with nothing in it has no height to claim, so `—` takes the
         top of the gutter and no stub. `-Infinity` is how it says that: it
         sorts above every rule rather than landing between two of them,
         which is where a lane with an 80 °C rule near its ceiling used to
         put it. */
      {
        key: 'value',
        kind: 'value' as const,
        anchorY: valueAnchor ?? -Infinity,
        rank: 0,
      },
      ...limitCallouts(limits, y, box),
    ],
    top + 9,
    top + laneH - 3,
    LINE_H,
  );
  const valueTag = placed.find((c) => c.kind === 'value')!;
  const rules = placed.filter((c): c is LimitCallout & { labelY: number | null } =>
    c.kind !== 'value');

  /* One horizontal reference every ~60px: a 45px lane gets its midline, a
     lane that has been isolated gets a proper ladder. Fewer than that and the
     trace is read against nothing; more and the grid competes with it. */
  const gridY = (channel.ticks ?? yTicks(box.lo, box.hi, Math.max(2, Math.round(plotH / 60))))
    .filter((v) => v > box.lo && v < box.hi)
    .filter((v) => !rules.some((l) => Math.abs(l.anchorY - y(v)) < 2));

  const printTick = (v: number) =>
    channel.tickText === undefined
      ? tickLabel(v, span, channel.digits)
      : channel.tickText(v, frame.tight);

  const colourFor = (lv: 'ok' | 'warn' | 'bad' | 'unknown') =>
    lv === 'bad' ? 'var(--error)' : lv === 'warn' ? 'var(--warn)' : colour;

  const tone = levelTone(curLevel);
  const dotFill =
    verdict === 'bad' ? 'var(--error)'
      : verdict === 'warn' ? 'var(--warn)'
        : verdict === 'unknown' ? 'transparent' : 'var(--nominal)';

  /* WHAT THE LEFT GUTTER SAYS, IN THE ORDER IT SAYS IT.

     1. the channel, with its verdict dot — WHICH subsystem, and is it bad
        (`auterion-flight-log-review.png`'s section index).
     2. the range this window actually held, with the unit. Not the axis:
        `5.0 – 59.6 %` is what the robot did, and it is the number an
        operator compares against another window.
     3. the published domain, where the axis is a magnification of it — so
        "the trace fills the lane" is never mistaken for "the value filled
        its range".
     4. the two ends of the y-box, right-aligned against the plot's edge at
        the exact heights they describe.

     Lines 2 and 3 are dropped rather than truncated when the gutter is too
     narrow to hold them: half a range is a number nobody can use. */
  const room = captionRoom(frame);
  /* WHAT THE WINDOW HELD, not what the plot was drawn against. The two are
     different numbers and both are load-bearing: the axis ends (right of
     this column, against the plot's edge) say where the ink can go, and this
     says where the robot actually went. Printing the box twice — which is
     what this line did for one round — spends a whole gutter row saying
     nothing. */
  const rangeText =
    samples.lo === null || samples.hi === null
      ? channel.unit
      : `${samples.lo.toFixed(channel.digits)} – ${samples.hi.toFixed(channel.digits)} ${channel.unit}`;
  const showRange = laneH >= 34 && monoFits(rangeText.length, 10, room);
  const domainText =
    box.fitted && channel.min !== undefined && channel.max !== undefined && !box.empty
      ? `of ${channel.min} – ${channel.max}`
      : channel.judgeNote !== undefined
        ? `on ${channel.judgeNote}`
        : null;
  const showDomain =
    domainText !== null && laneH >= 46 && monoFits(domainText.length, 10, room);
  /* The distribution, as numbers, wherever a lane has been isolated and has
     the height for it — beside the picture it describes rather than inside
     it, and never over the trace.

     TWO COLUMNS, MEASURED, NOT A GUESSED BOX. This block right-aligned its
     values inside a fixed 46px field for three rounds, and 46px is not
     enough: `p50` inks 18.1px from the name column and a five-glyph `110.9`
     inks 30.1px back from the field's edge, so the two ran through each
     other by 2.2px and only the four-glyph `81.5` escaped. The label column
     is now as wide as its widest label, the value column reserves
     STAT_DIGITS glyphs so it cannot jitter as the window's numbers change
     length, and STAT_GAP sits between them. */
  const statLabelW = statRows === null
    ? 0
    : monoWidth(Math.max(...statRows.map(([k]) => k.length)), 10);
  const statValueW = statRows === null
    ? 0
    : monoWidth(Math.max(STAT_DIGITS, ...statRows.map(([, v]) => v.length)), 10);
  /* Right edge of the value column, and therefore the whole block's width. */
  const statValueX = frame.nameX + statLabelW + STAT_GAP + statValueW;
  const showStats =
    statRows !== null && plotH >= 96 && !frame.tight && statValueX <= frame.axisX - 6;
  const statTop = top + (showDomain ? DOMAIN_DY + LINE_H : RANGE_DY + LINE_H) + 6;

  const axisTopY = y0 + 8;
  const axisBotY = y0 + plotH - 1;

  /* WHICH interior ticks are allowed to print their number. A gridline is
     cheap and always drawn; a label has to clear the axis's two ends and its
     own neighbours by one pitch or it is not a second number, it is the
     first one smudged. Dropping the label and keeping the line is the same
     trade the right gutter makes when a lane runs out of room. */
  const tickLabels: { v: number; gy: number }[] = [];
  for (const v of gridY) {
    const gy = y(v) + 3;
    if (gy < axisTopY + MIN_PITCH || gy > axisBotY - MIN_PITCH) continue;
    const prev = tickLabels[tickLabels.length - 1];
    if (prev !== undefined && Math.abs(gy - prev.gy) < MIN_PITCH) continue;
    tickLabels.push({ v, gy });
  }

  return (
    <g>
      {top > 0 && (
        <line
          x1={0} x2={frame.width} y1={top} y2={top}
          stroke="var(--line)" strokeWidth={1} shapeRendering="crispEdges"
        />
      )}

      {/* ---- the left gutter ------------------------------------------- */}
      <circle
        cx={6}
        cy={top + NAME_DY - 3}
        r={3}
        fill={dotFill}
        stroke={verdict === 'unknown' ? 'var(--unknown)' : 'none'}
        strokeWidth={1}
      />
      <text className={styles.gutterName} x={frame.nameX} y={top + NAME_DY}>
        {channel.label}
      </text>
      {showRange && (
        <text className={styles.gutterRange} x={frame.nameX} y={top + RANGE_DY}>
          {rangeText}
        </text>
      )}
      {showDomain && (
        <text className={styles.gutterNote} x={frame.nameX} y={top + DOMAIN_DY}>
          {domainText}
        </text>
      )}
      {showStats &&
        statRows!.map(([k, v], si) => (
          <g key={k}>
            <text className={styles.gutterNote} x={frame.nameX} y={statTop + si * LINE_H}>
              {k}
            </text>
            <text
              className={styles.gutterStat}
              x={statValueX}
              y={statTop + si * LINE_H}
              textAnchor="end"
            >
              {v}
            </text>
          </g>
        ))}

      {/* The y-box's two ends, right-aligned on the plot's left edge at the
          heights they describe. Suppressed on an empty window: `0.0 – 1.0
          GiB` for a robot that never reported is an axis the contract never
          published. */}
      {!box.empty && axisBotY - axisTopY >= MIN_PITCH && (
        <>
          {/* A 3px stub at the plot's edge, at the exact height the number
              describes. Without it an axis end printed level with the lane's
              name reads as part of the name; with it, it is unmistakably an
              edge of the box. */}
          <line
            x1={frame.x0 - 3} x2={frame.x0} y1={axisTopY - 3} y2={axisTopY - 3}
            stroke="var(--line-3)" strokeWidth={1} shapeRendering="crispEdges"
          />
          <line
            x1={frame.x0 - 3} x2={frame.x0} y1={axisBotY - 3} y2={axisBotY - 3}
            stroke="var(--line-3)" strokeWidth={1} shapeRendering="crispEdges"
          />
          {/* THE ENDS ARE BARE NUMBERS. The unit is on the range line
              directly below, and an axis end that carries one sits level
              with the lane's name and reads as a reading of it — `Speed
              0.50 m/s` is a sentence the plot never said. The stub and the
              column say it is an axis; the numbers say where. */}
          <text className={styles.gutterAxis} x={frame.axisX} y={axisTopY} textAnchor="end">
            {printTick(box.hi)}
          </text>
          <text className={styles.gutterAxis} x={frame.axisX} y={axisBotY} textAnchor="end">
            {printTick(box.lo)}
          </text>
        </>
      )}

      {/* Interior grid values, wherever the lane is tall enough to hold one
          clear of both ends. Same column as the ends, one step dimmer.

          THE ENDS WIN. An interior tick is a convenience; the box's ends are
          the axis, and a nice number that lands within one pitch of one of
          them prints a second label through it — on an isolated Disk free
          lane the domain end `78` and the tick `80` sat 10.1px apart for
          10px type and their ink boxes overlapped. The tick's LABEL is
          dropped and its gridline stays, which is the same trade the right
          gutter makes. */}
      {plotH >= 74 &&
        tickLabels.map(({ v, gy }) => (
          <text
            key={`gl${v}`}
            className={styles.gutterTick}
            x={frame.axisX}
            y={gy}
            textAnchor="end"
          >
            {printTick(v)}
          </text>
        ))}

      {/* ---- the right gutter ------------------------------------------ */}
      {/* THE READING IS A CALLOUT ON ITS OWN TRACE, not a caption at the top
          of the lane. Its stub sits at the height of the sample it is
          quoting, drawn in that sample's colour, so a battery below both of
          its rules prints below both of their labels. Number and unit are
          one readout but two columns, so `79.9 %`, `0.46 m/s` and `81.5 GiB`
          align on their digits down the whole stack (§4) — and every
          threshold label uses the same two columns. */}
      <GutterCallout
        frame={frame}
        anchorY={valueAnchor}
        labelY={valueTag.labelY}
        stroke={colourFor(curLevel)}
        variant="reading"
        strokeOpacity={stale ? 0.5 : 1}
        value={cur == null ? NO_VALUE : cur.toFixed(channel.digits)}
        unit={cur == null ? null : channel.unit}
        valueProps={{ 'data-tone': cur == null ? 'neutral' : tone, 'data-stale': stale }}
        unitProps={{ 'data-stale': stale }}
      />

      {/* A window with nothing in it still shows where the limits are — the
          channels exist and so do their rules — but at half ink, because a
          limit no trace can be judged against is not in play. */}
      {rules.map((l) => (
        <g key={l.kind} data-limit={l.kind} opacity={box.empty ? 0.5 : 1}>
          <GutterCallout
            frame={frame}
            anchorY={l.anchorY}
            labelY={l.labelY}
            stroke={l.kind === 'bad' ? 'var(--error)' : 'var(--warn)'}
            variant="limit"
            value={String(l.v)}
            unit={channel.unit}
            valueProps={{ 'data-kind': l.kind }}
            unitProps={{ 'data-kind': l.kind }}
          />
        </g>
      ))}

      {/* ---- the plot -------------------------------------------------- */}
      <g transform={`translate(${frame.x0},0)`}>
        {/* THE FLOOR, WHERE THE FLOOR IS A REAL NUMBER.

            A robot parked on the charger reports 0.00 m/s for three hours, and
            without this the trace lies exactly on the lane's lower boundary and
            the lane reads as EMPTY rather than as "pinned at zero" — the one
            reading an operator must never mistake for missing data. Drawn only
            when the box's lower end is a bound the quantity actually has, never
            under a fitted axis where it would be a claim about nothing. */}
        {channel.min !== undefined && box.lo === channel.min && (
          <line
            x1={0} x2={width} y1={y(box.lo)} y2={y(box.lo)}
            stroke="var(--line-2)" strokeWidth={1} shapeRendering="crispEdges"
          />
        )}

        {gridY.map((v) => (
          <line
            key={`g${v}`}
            x1={0} x2={width} y1={y(v)} y2={y(v)}
            stroke="var(--plot-grid)" strokeWidth={1} shapeRendering="crispEdges"
          />
        ))}

        {rules.map((l) => (
          <line
            key={l.kind}
            x1={0} x2={width} y1={l.anchorY} y2={l.anchorY}
            stroke={l.kind === 'bad' ? 'var(--error)' : 'var(--warn)'}
            strokeOpacity={crossed[l.kind] ? 0.9 : 0.4}
            strokeWidth={1}
            strokeDasharray="1 3"
            shapeRendering="crispEdges"
          />
        ))}

        {trace.segments.map((s, k) => (
          <path
            key={`${k}-${s.d.length}`}
            d={s.d}
            fill="none"
            stroke={colourFor(s.level)}
            strokeWidth={1.4}
            strokeLinecap="butt"
            strokeLinejoin="round"
          />
        ))}

        {/* Samples a break left on their own. Without these a heading that
            wraps every other reading loses half its measurements to the
            seam. */}
        {trace.points.map((p, k) => (
          <circle key={`p${k}`} cx={p.x} cy={p.y} r={1.4} fill={colourFor(p.level)} />
        ))}

        {/* WHERE THE SAMPLES ACTUALLY ARE.

            A polyline through 25 readings and a polyline through 600 draw the
            same picture, and the operator has no way to tell a 2-minute cadence
            from a 2-second one by looking. Once the samples are far enough apart
            to be resolved, they are drawn — so a 1h window on a slow agent reads
            as the two dozen measurements it is, rather than as a continuous
            signal the console does not have. */}
        {sparse &&
          samples.t.map((ts, k) =>
            samples.v[k] == null ? null : (
              <circle
                key={ts}
                cx={x(ts)}
                cy={y(samples.v[k] as number)}
                r={1.6}
                fill={colourFor(samples.lv[k]!)}
              />
            ),
          )}

        {/* The sample the readout is quoting, marked on the trace. It is drawn
            for a pinned cursor and for a stale edge quote — the two cases where
            the number beside the lane came from somewhere other than the trace's
            right end and the operator should be able to see where. */}
        {i >= 0 && cur != null && (at !== null || stale) && (
          <circle
            cx={x(samples.t[i]!)}
            cy={y(cur)}
            r={2.5}
            fill={colourFor(curLevel)}
            stroke="var(--bg-1)"
            strokeWidth={1}
          />
        )}
      </g>
    </g>
  );
}
